import ChatMarkdown from "~/components/ChatMarkdown";
import { MarkdownContent } from "~/components/rich-content/MarkdownContent";
import { usePrimaryEnvironmentId } from "~/environments/primary";
import { useSavedEnvironmentRuntimeStore } from "~/environments/runtime/savedEnvironmentRuntimeStore";
import { cn } from "~/lib/utils";
import { usePullRequestHandoffController } from "~/pullRequest/handoff/PullRequestHandoffProvider";
import { useServerConfig } from "~/rpc/serverState";

export interface PullRequestMarkdownProps {
  readonly text: string;
  readonly className?: string;
}

/**
 * PR prose uses the selected repository-matched execution environment for file actions.
 * Command remains the PR authority; without a ready matching checkout, Markdown stays inert.
 */
export function PullRequestMarkdown(props: PullRequestMarkdownProps) {
  const handoff = usePullRequestHandoffController();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryServerConfig = useServerConfig();
  const targetEnvironmentId = handoff?.targetProject.environmentId ?? null;
  const savedServerConfig = useSavedEnvironmentRuntimeStore((state) =>
    targetEnvironmentId === null || targetEnvironmentId === primaryEnvironmentId
      ? null
      : (state.byId[targetEnvironmentId]?.serverConfig ?? null),
  );
  const targetServerConfig =
    targetEnvironmentId === primaryEnvironmentId ? primaryServerConfig : savedServerConfig;
  const canRevealFiles =
    targetServerConfig?.shellRevealInFileManager === true &&
    targetServerConfig.availableEditors.includes("file-manager");
  return handoff === null || !canRevealFiles ? (
    <MarkdownContent
      variant="compact"
      allowImages={false}
      {...(props.className === undefined ? {} : { className: props.className })}
    >
      {props.text}
    </MarkdownContent>
  ) : (
    <div className={cn("min-w-0", props.className)}>
      <ChatMarkdown
        text={props.text}
        cwd={handoff.targetProject.cwd}
        environmentId={handoff.targetProject.environmentId}
        {...(targetServerConfig?.shellRevealInFileManagerKind === undefined
          ? {}
          : { fileManagerRevealKind: targetServerConfig.shellRevealInFileManagerKind })}
      />
    </div>
  );
}
